import { useCallback, useState } from 'react';
import {
    Box,
    CircularProgress,
    IconButton,
    Stack,
    TextField,
    Typography,
    Paper,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDropzone } from 'react-dropzone';
import { QuoteAttachmentPreviewDialog } from '@/components/QuoteAttachmentPreviewDialog';
import {
    defaultAttachmentName,
    deleteQuoteAttachment,
    isLocalAttachmentId,
    QuoteAttachment,
    uploadQuoteAttachment,
} from '@/utils/quote_attachment_utils';

const ACCEPT = {
    'application/pdf': ['.pdf'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
};

type AttachmentSectionProps = {
    title: string;
    description: string;
    attachments: QuoteAttachment[];
    onChange: (attachments: QuoteAttachment[]) => void;
};

const AttachmentSection = ({
    title,
    description,
    attachments,
    onChange,
}: AttachmentSectionProps) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState<QuoteAttachment | null>(null);

    const handleFiles = useCallback(async (files: File[]) => {
        if (!files.length) return;
        setUploading(true);
        setError('');
        const added: QuoteAttachment[] = [];
        try {
            for (const file of files) {
                const result = await uploadQuoteAttachment(file);
                added.push({
                    id: result.attachment_id,
                    name: defaultAttachmentName(result.filename),
                    filename: result.filename,
                    content_type: result.content_type,
                    uploaded_at: new Date().toISOString(),
                });
            }
            onChange([...attachments, ...added]);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Upload failed');
        } finally {
            setUploading(false);
        }
    }, [attachments, onChange]);

    const handleRemove = async (attachment: QuoteAttachment) => {
        setError('');
        try {
            if (isLocalAttachmentId(attachment.id)) {
                await deleteQuoteAttachment(attachment.id);
            }
            onChange(attachments.filter(a => a.id !== attachment.id));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Delete failed');
        }
    };

    const handleRename = (id: string, name: string) => {
        onChange(attachments.map(a => (a.id === id ? { ...a, name } : a)));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: ACCEPT,
        multiple: true,
        disabled: uploading,
        onDrop: files => void handleFiles(files),
    });

    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Box>

            <Box
                {...getRootProps()}
                sx={{
                    py: 4,
                    px: 2,
                    textAlign: 'center',
                    border: '1px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    bgcolor: isDragActive ? 'action.hover' : 'transparent',
                    cursor: uploading ? 'wait' : 'pointer',
                    opacity: uploading ? 0.7 : 1,
                }}
            >
                <input {...getInputProps()} />
                {uploading ? (
                    <CircularProgress size={24} />
                ) : (
                    <>
                        <AttachFileIcon sx={{ color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            {isDragActive
                                ? 'Drop files here…'
                                : 'Drag and drop files here, or click to browse'}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                            PDF, JPG, PNG
                        </Typography>
                    </>
                )}
            </Box>

            {error && (
                <Typography variant="body2" color="error">
                    {error}
                </Typography>
            )}

            {attachments.map(attachment => (
                <Stack
                    key={attachment.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'white',
                    }}
                >
                    <TextField
                        size="small"
                        label="Name"
                        value={attachment.name}
                        onChange={e => handleRename(attachment.id, e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                        {attachment.filename}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => setPreview(attachment)}
                        aria-label="Preview attachment"
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => void handleRemove(attachment)}
                        aria-label="Remove attachment"
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ))}

            <QuoteAttachmentPreviewDialog
                attachment={preview}
                onClose={() => setPreview(null)}
            />
        </Stack>
    );
};

type Props = {
    clientEstimateAttachments: QuoteAttachment[];
    onClientEstimateChange: (files: QuoteAttachment[]) => void;
    relatedQuoteFiles: QuoteAttachment[];
    onRelatedQuoteChange: (files: QuoteAttachment[]) => void;
    viewFiles: QuoteAttachment[];
    onViewFilesChange: (files: QuoteAttachment[]) => void;
    quoteId?: number | null;
    isLegacy?: boolean;
};

export const QuoteAttachmentsTab = ({
    clientEstimateAttachments,
    onClientEstimateChange,
    relatedQuoteFiles,
    onRelatedQuoteChange,
    viewFiles,
    onViewFilesChange,
    quoteId,
    isLegacy = false,
}: Props) => (
    <Paper variant="outlined" sx={{
        p: 3, bgcolor: 'white', mx: 'auto', minWidth: 500,
        fontFamily: 'Arial, sans-serif',
    }}>
        <Stack spacing={4}>
            <AttachmentSection
                title="Client estimate final file"
                description="Upload PDF or image files. They will be appended to the end of the client estimate PDF."
                attachments={clientEstimateAttachments}
                onChange={onClientEstimateChange}
            />
            <AttachmentSection
                title="Related quote files"
                description="Upload supporting files for this quote. These are not included in the client estimate PDF."
                attachments={relatedQuoteFiles}
                onChange={onRelatedQuoteChange}
            />
            <AttachmentSection
                title="Views"
                description="Measurements plans and calculated PDFs. Saved to OneDrive Views when you save the quote (not included in the client estimate PDF)."
                attachments={viewFiles}
                onChange={onViewFilesChange}
            />
        </Stack>
    </Paper>
);
